import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { useMinuteClock } from "@/hooks/useMinuteClock";
import type { DriverRow } from "@/types/driver";
import { CoordinatesLink } from "@/ui/CoordinatesLink";
import { zinc } from "@/ui/tokens";
import { LOCATION_TOOLTIP_ENTER_DELAY, locationProvenanceLabel, locationStaleness } from "@/utils/locationStaleness";
import { googleMapsUrl, locationText } from "@/utils/locationText";
import { ContextField, ContextText } from "./ContextBlock";
import { NO_VALUE } from "./contextFormat";

export { googleMapsUrl };

/**
 * The driver's location as `locationText` names it, with the country under it and, when the device reported a fix,
 * a link that opens it in Google Maps so a dispatcher can see where the driver is rather than read it. The
 * coordinates fallback links to the same map, as it does in the roster and the Dispatch table.
 *
 * The state is not repeated under the place: the location text already ends in it. The location's source and age show
 * on hover and again on a line of their own under the country, and a stale location reads in a dark grey.
 */
export default function LocationField({ driver }: { driver: DriverRow }) {
  const now = useMinuteClock();
  const staleness = locationStaleness(driver.locationUpdatedAt, now);
  const shown = locationText(driver);
  const provenance = locationProvenanceLabel(staleness, driver.locationSource);
  const mapsUrl =
    driver.latitude != null && driver.longitude != null ? googleMapsUrl(driver.latitude, driver.longitude) : null;
  const value = (
    <ContextText variant="value" sx={shown != null && staleness.stale ? { color: zinc[600] } : undefined}>
      {shown?.coordinates ? (
        <CoordinatesLink latitude={driver.latitude} longitude={driver.longitude}>
          {shown.text}
        </CoordinatesLink>
      ) : (
        (shown?.text ?? NO_VALUE)
      )}
    </ContextText>
  );
  return (
    <ContextField
      icon={<LocationOnOutlinedIcon sx={{ fontSize: 18 }} />}
      label="Location"
      testId={TestIds.dispatch.contextLocation}
    >
      {shown != null ? (
        <Tooltip
          title={provenance}
          enterDelay={LOCATION_TOOLTIP_ENTER_DELAY.toMillis()}
          enterNextDelay={LOCATION_TOOLTIP_ENTER_DELAY.toMillis()}
        >
          <Box>{value}</Box>
        </Tooltip>
      ) : (
        value
      )}
      {(driver.country || mapsUrl) && (
        <ContextText variant="subtext">
          {driver.country}
          {driver.country && mapsUrl ? " · " : ""}
          {mapsUrl && (
            <Link
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color: "var(--color-brand-primary-400)" }}
            >
              Google Maps
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          )}
        </ContextText>
      )}
      {shown != null && (
        <ContextText variant="subtext" testId={TestIds.dispatch.contextLocationProvenance}>
          {provenance}
        </ContextText>
      )}
    </ContextField>
  );
}
